import { useState, useCallback } from 'react';
import { useAccount, useChainId, useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt } from 'wagmi/actions';
import { parseEther, formatEther } from 'viem';
import ERC20_ABI from '../utils/abis/erc20TokenABI.json';
import { publicClients } from '../lib/wagmi';


export const useTokenAllowance = () => {
  const [currentAllowance, setCurrentAllowance] = useState(null);
  const [isCheckingAllowance, setIsCheckingAllowance] = useState(false);
  const [isApprovingToken, setIsApprovingToken] = useState(false);
  const chainId = useChainId();
  const config = useConfig();
  const { address: ownerAddress } = useAccount();

  const checkAllowance = useCallback(
    async (tokenAddress, spenderAddress) => {

      // console.log(tokenAddress, ownerAddress, spenderAddress, 'params in checkAllowance');
      // if ( !tokenAddress || !ownerAddress || !spenderAddress) return null;
      try {

        // console.log(tokenAddress, ownerAddress, spenderAddress, 'params in checkAllowance');
        setIsCheckingAllowance(true);
        const allowance = await publicClients[chainId].readContract({
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'allowance',
          args: [ownerAddress, spenderAddress],
        });
        setCurrentAllowance(allowance);
        return allowance;
      } catch (error) {
        console.error('Error checking allowance:', error);
        setCurrentAllowance(null);
        return null;
      } finally {
        setIsCheckingAllowance(false);
      }
    },
    [chainId, ownerAddress]
  );

  const approveToken = useCallback(
    async (tokenAddress, spenderAddress, amount) => {
      try {
        setIsApprovingToken(true);
        const amountWei = parseEther(amount.toString());
        const hash = await writeContract(config, {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [spenderAddress, amountWei],
        });
        const receipt = await waitForTransactionReceipt(config, { hash });
        setCurrentAllowance(amountWei);
        return { receipt, hash };
      } catch (error) {
        console.error('Error approving token:', error);
        throw error;
      } finally {
        setIsApprovingToken(false);
      }
    },
    [config]
  );

  const hasInsufficientAllowance = useCallback(
    (requiredAmount) => {
      if (currentAllowance === null || currentAllowance === undefined) return true;
      const allowedEth = Number(formatEther(currentAllowance));
      const requiredEth = Number(requiredAmount);
      if (Number.isNaN(allowedEth) || Number.isNaN(requiredEth)) return true;
      return allowedEth < requiredEth;
    },
    [currentAllowance]
  );

  const ensureAllowance = useCallback(
    async (tokenAddress, spenderAddress, requiredAmount) => {
      const allowance = await checkAllowance(tokenAddress, spenderAddress);
      // console.log(allowance, 'allowance in ensureAllowance');
      if (allowance === null) throw new Error('Unable to check allowance');
      if (hasInsufficientAllowance(requiredAmount)) {
        await approveToken(tokenAddress, spenderAddress, requiredAmount);
      }
      return true;
    },
    [checkAllowance, hasInsufficientAllowance, approveToken]
  );

  return {
    currentAllowance,
    isCheckingAllowance,
    isApprovingToken,
    checkAllowance,
    approveToken,
    ensureAllowance,
    hasInsufficientAllowance, 
  };
};